/**
 * Sticky left overview for the adaptive screener (PRD §8.1). Mirrors the intake
 * call guide's {@link IntakeGuideWayfinding} rail: persistent segment list,
 * active-section highlight, and click-to-jump. Adds settlement progress and
 * instrument · gating subtitles per the assessments mockup.
 *
 * @agentic connections
 */

import {
  Badge,
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCircle,
  IconCircleCheck,
  IconCircleDashed,
} from "@tabler/icons-react";

import { STANDARD_QUESTIONNAIRES } from "@benchmark/core/constants";
import type {
  AdaptiveSection,
  AdaptiveSectionKey,
  AdaptiveSectionReasonCode,
  AdaptiveSectionStatus,
} from "@benchmark/core/services";

/** Human-readable section titles for the rail and accordion. */
export const sectionTitle = (section: AdaptiveSection): string => {
  if (section.key === "patient-rating") {
    return section.instruments[0] === "PGI_C"
      ? "Patient's own rating of change"
      : "Patient's own rating of severity";
  }
  return SECTION_TITLES[section.key];
};

const SECTION_TITLES: Record<AdaptiveSectionKey, string> = {
  "patient-rating": "Patient's own rating",
  depression: "Depression",
  "suicide-risk": "Suicide risk",
  "safety-plan": "Safety plan",
  anxiety: "Anxiety",
  loneliness: "Loneliness",
  functioning: "Everyday functioning",
  alcohol: "Alcohol use",
  "drug-use": "Drug use",
};

const REASON_SHORT: Record<AdaptiveSectionReasonCode, string> = {
  everyone: "Everyone",
  "short-check": "Short check first",
  "opened-at-baseline": "Asked in full",
  triggered: "Triggered now",
};

/** Instrument label for the overview subtitle (mockup uses the full-set name). */
export const instrumentLabel = (section: AdaptiveSection): string => {
  const key = section.instruments[section.instruments.length - 1];
  return STANDARD_QUESTIONNAIRES[key]?.name ?? key;
};

export const sectionSubtitle = (section: AdaptiveSection): string =>
  `${instrumentLabel(section)} · ${REASON_SHORT[section.reasonCode]}`;

const statusIcon = (status: AdaptiveSectionStatus) => {
  const label =
    status === "answered"
      ? "Answered"
      : status === "pending"
        ? "Not settled"
        : SETTLEMENT_ARIA[status];
  if (status === "answered") {
    return (
      <ThemeIcon
        size="xs"
        variant="transparent"
        color="green"
        aria-label={label}
      >
        <IconCircleCheck size={14} />
      </ThemeIcon>
    );
  }
  if (status !== "pending") {
    return (
      <ThemeIcon
        size="xs"
        variant="transparent"
        color="gray"
        aria-label={label}
      >
        <IconCircle size={14} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon size="xs" variant="transparent" color="gray" aria-label={label}>
      <IconCircleDashed size={14} />
    </ThemeIcon>
  );
};

const SETTLEMENT_ARIA: Record<
  Exclude<AdaptiveSectionStatus, "pending" | "answered">,
  string
> = {
  "declined-remaining": "Patient declined the rest",
  "declined-section": "Patient declined",
  "not-administered-clinical": "Not administered — clinical judgement",
};

/**
 * Props for {@link AdaptiveScreenerWayfinding}.
 * @agentic connections
 */
export type AdaptiveScreenerWayfindingProps = {
  sections: readonly AdaptiveSection[];
  activeSectionKey?: AdaptiveSectionKey | null;
  onJump: (sectionKey: AdaptiveSectionKey) => void;
  settledCount: number;
  totalCount: number;
  answeredQuestionCount: number;
  approxQuestionCount: number;
  statusFor: (section: AdaptiveSection) => AdaptiveSectionStatus;
};

/**
 * Persistent glanceable overview of today's adaptive screener sections.
 * @agentic connections
 */
export const AdaptiveScreenerWayfinding = ({
  sections,
  activeSectionKey,
  onJump,
  settledCount,
  totalCount,
  answeredQuestionCount,
  approxQuestionCount,
  statusFor,
}: AdaptiveScreenerWayfindingProps) => (
  <Box
    data-testid="adaptive-screener-wayfinding"
    style={{
      position: "sticky",
      top: "var(--mantine-spacing-md)",
      alignSelf: "flex-start",
      flex: "0 0 260px",
      maxWidth: 260,
      maxHeight: "calc(100vh - var(--mantine-spacing-xl))",
      display: "flex",
      flexDirection: "column",
      gap: "var(--mantine-spacing-sm)",
    }}
  >
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap">
        <Text fw={600} size="sm">
          Today&apos;s screener
        </Text>
        <Badge
          variant="light"
          size="sm"
          data-testid="adaptive-wayfinding-progress"
        >
          {settledCount}/{totalCount}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed" data-testid="adaptive-wayfinding-questions">
        {answeredQuestionCount} of about {approxQuestionCount} questions
      </Text>
    </Stack>

    <Box
      component="nav"
      aria-label="Screener sections"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <Stack gap={2}>
        {sections.map((section) => {
          const active = section.key === activeSectionKey;
          const status = statusFor(section);
          return (
            <UnstyledButton
              key={section.key}
              onClick={() => onJump(section.key)}
              data-testid={`adaptive-wayfinding-${section.key}`}
              aria-current={active ? "true" : undefined}
              style={{
                padding: "6px 10px",
                borderRadius: "var(--mantine-radius-sm)",
                borderLeft: active
                  ? "3px solid var(--mantine-primary-color-filled)"
                  : "3px solid transparent",
                background: active
                  ? "var(--mantine-primary-color-light)"
                  : "transparent",
              }}
            >
              <Group gap={6} wrap="nowrap" align="flex-start">
                {statusIcon(status)}
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text
                    size="xs"
                    fw={active ? 700 : 500}
                    c={active ? undefined : "gray.7"}
                    style={
                      active
                        ? { color: "var(--mantine-primary-color-light-color)" }
                        : undefined
                    }
                  >
                    {sectionTitle(section)}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {sectionSubtitle(section)}
                  </Text>
                </Stack>
              </Group>
            </UnstyledButton>
          );
        })}
      </Stack>
    </Box>
  </Box>
);
